import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadFile } from "../../hooks";
import ConfirmDelete from "./ConfirmDelete";
import MwButton from "./MwButton";
import MwModal from "./MwModal";

const splitLocations = (value) =>
  String(value || "")
    .split(/[;,]/)
    .map((file) => String(file || "").trim())
    .filter(Boolean);

const normalizeFiles = (files = []) => {
  const seen = new Set();
  return files
    .map((file) => String(file || "").trim())
    .filter((file) => {
      if (!file || seen.has(file)) return false;
      seen.add(file);
      return true;
    });
};

const isSameList = (first = [], second = []) =>
  first.length === second.length &&
  first.every((item, index) => item === second[index]);

const resolveFileName = (fileUrl) => {
  const raw = String(fileUrl || "");
  const withoutQuery = raw.split("?")[0];
  const fromPath = withoutQuery.split("/").pop() || withoutQuery;
  try {
    return decodeURIComponent(fromPath);
  } catch (error) {
    return fromPath;
  }
};

const resolveFileExtension = (fileName) => {
  const parts = String(fileName || "").split(".");
  const extension = parts.length > 1 ? parts.pop() : "";
  return String(extension || "")
    .trim()
    .toUpperCase();
};

const IMAGE_PREVIEW_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "svg"];

const AttachedFiles = ({
  files,
  onChange,
  disabled = false,
  state,
  setState,
}) => {
  const {
    location,
    loading,
    loadingDeleting,
    handleMultiUploads,
    handleDelete,
  } = useUploadFile("*", "accounting-transactions");

  const [t] = useTranslation("global");
  const lastProcessedLocationRef = useRef("");
  const [previewFile, setPreviewFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const currentFiles = useMemo(
    () =>
      normalizeFiles(
        Array.isArray(files)
          ? files
          : Array.isArray(state?.attach_files)
            ? state.attach_files
            : [],
      ),
    [files, state?.attach_files],
  );

  const applyFiles = useCallback(
    (nextFiles) => {
      const normalized = normalizeFiles(nextFiles);
      if (typeof onChange === "function") {
        onChange(normalized);
        return;
      }
      if (typeof setState === "function") {
        setState((prev) => ({
          ...prev,
          attach_files: normalized,
        }));
      }
    },
    [onChange, setState],
  );

  React.useEffect(() => {
    if (!location || location === lastProcessedLocationRef.current) return;
    lastProcessedLocationRef.current = location;

    const uploadedFiles = splitLocations(location);
    if (uploadedFiles.length === 0) return;

    const mergedFiles = normalizeFiles([...currentFiles, ...uploadedFiles]);
    if (isSameList(mergedFiles, currentFiles)) return;

    applyFiles(mergedFiles);
  }, [location, currentFiles, applyFiles]);

  const resolveTone = (extension) => {
    const ext = String(extension || "").toLowerCase();
    if (["pdf"].includes(ext)) {
      return {
        bg: "#FEE2E2",
        text: "#991B1B",
        border: "#FECACA",
      };
    }
    if (["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext)) {
      return {
        bg: "#DCFCE7",
        text: "#166534",
        border: "#BBF7D0",
      };
    }
    if (["xls", "xlsx", "csv"].includes(ext)) {
      return {
        bg: "#DBEAFE",
        text: "#1E3A8A",
        border: "#BFDBFE",
      };
    }
    return {
      bg: "#F1F5F9",
      text: "#334155",
      border: "#CBD5E1",
    };
  };
  const extractKey = (url) => {
    try {
      const parts = url.split("/uploads/");
      return parts[1];
    } catch {
      return url;
    }
  };

  const closePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  const openPreview = useCallback(
    (fileUrl) => {
      const fileName =
        resolveFileName(fileUrl) || t("JournalEntry.attachments.fileFallback");
      const extension = resolveFileExtension(fileName);
      setPreviewFile({
        url: fileUrl,
        name: fileName,
        extension: String(extension || "").toLowerCase(),
      });
    },
    [t],
  );

  const openDeleteConfirm = useCallback(
    (fileUrl) => {
      const fileName =
        resolveFileName(fileUrl) || t("JournalEntry.attachments.fileFallback");
      setFileToDelete({
        url: fileUrl,
        name: fileName,
      });
      setShowDeleteConfirm(true);
    },
    [t],
  );

  const closeDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  }, []);

  const handleDeleteConfirmVisibility = useCallback((visible) => {
    const shouldShow = Boolean(visible);
    setShowDeleteConfirm(shouldShow);
    if (!shouldShow) {
      setFileToDelete(null);
    }
  }, []);

  const confirmDeleteFile = useCallback(() => {
    if (!fileToDelete?.url) return;

    const key = extractKey(fileToDelete.url);
    handleDelete(key);
    applyFiles(currentFiles.filter((file) => file !== fileToDelete.url));

    if (previewFile?.url === fileToDelete.url) {
      setPreviewFile(null);
    }

    closeDeleteConfirm();
  }, [
    applyFiles,
    closeDeleteConfirm,
    currentFiles,
    fileToDelete?.url,
    handleDelete,
    previewFile?.url,
  ]);

  const previewExtension = String(previewFile?.extension || "").toLowerCase();
  const isImagePreview = IMAGE_PREVIEW_EXTENSIONS.includes(previewExtension);
  const isPdfPreview = previewExtension === "pdf";

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/40 p-3.5 shadow-sm">
        <div className="flex mt-1  flex-col md:flex-row gap-3.5">
          <label
            className={`md:w-56 w-full  shrink-0 rounded-xl border-2 border-dashed h-fit p-3.5 transition ${
              disabled || loading
                ? "cursor-not-allowed opacity-70"
                : "cursor-pointer hover:bg-white hover:shadow-sm"
            }`}
            style={{ borderColor: "#8D82B6" }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center text-white text-lg font-semibold"
                style={{ backgroundColor: "#261759" }}
              >
                +
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#261759] leading-5 truncate">
                  {loading
                    ? t("JournalEntry.attachments.uploading")
                    : t("JournalEntry.attachments.uploadHint")}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {Array.isArray(currentFiles) ? currentFiles.length : 0}{" "}
                  {t("JournalEntry.attachments.fileBadge")}
                </p>
              </div>
            </div>

            <input
              type="file"
              multiple
              disabled={disabled || loading}
              onChange={(event) => {
                handleMultiUploads(event);
                event.target.value = null;
              }}
              className="hidden"
            />
          </label>

          <div className=" flex-1  w-full">
            {currentFiles.length > 0 ? (
              <div className=" grid grid-cols-2 items-center gap-1 overflow-auto  pr-1">
                {currentFiles.map((file) => {
                  const fileName =
                    resolveFileName(file) ||
                    t("JournalEntry.attachments.fileFallback");
                  const extension =
                    resolveFileExtension(fileName) ||
                    t("JournalEntry.attachments.fileBadge");
                  const tone = resolveTone(extension);

                  return (
                    <div
                      key={file}
                      className="group rounded-xl border border-slate-200 bg-white px-3 py-2.5 transition hover:border-slate-300 hover:shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-8 h-8 shrink-0 rounded-md border flex items-center justify-center text-[10px] font-semibold tracking-wide"
                            style={{
                              backgroundColor: tone.bg,
                              color: tone.text,
                              borderColor: tone.border,
                            }}
                          >
                            {String(extension).slice(0, 4)}
                          </div>
                          <p
                            className="text-sm font-medium text-slate-700 truncate"
                            title={fileName}
                          >
                            {fileName}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openPreview(file)}
                            className="text-[11px] font-semibold text-blue-700 border border-blue-200 bg-blue-50/50 hover:bg-blue-100 rounded-md px-2 py-1 transition"
                          >
                            {t("JournalEntry.attachments.view")}
                          </button>
                          {!disabled && (
                            <button
                              type="button"
                              onClick={() => openDeleteConfirm(file)}
                              className="text-[11px] font-semibold text-red-700 border border-red-200 bg-red-50/50 hover:bg-red-100 rounded-md px-2 py-1 transition"
                            >
                              {t("JournalEntry.attachments.remove")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-full min-h-[90px] rounded-xl border border-dashed border-slate-300 bg-white px-3 flex items-center justify-center text-sm text-slate-500">
                {t("JournalEntry.attachments.empty")}
              </div>
            )}
          </div>
        </div>
      </div>

      <MwModal
        onShow={Boolean(previewFile)}
        setOnShow={closePreview}
        custWidth={"md:w-5/6"}
      >
        <div className="flex w-full h-full flex-col bg-slate-50">
          <div className="flex items-center justify-between gap-3 border-b border-slate-300 bg-white px-4 py-3">
            <h3
              className="text-[#261759] font-semibold text-sm truncate"
              title={previewFile?.name || ""}
            >
              {previewFile?.name || t("JournalEntry.attachments.fileFallback")}
            </h3>

            <div className="flex items-center gap-2">
            
              <MwButton type="secondary" onClick={closePreview}>
                {t("common.cancel")}
              </MwButton>
            </div>
          </div>

          <div className="h-[75vh] p-4">
            {isImagePreview ? (
              <div className="h-full w-full rounded-xl border border-slate-200 bg-white p-3 overflow-auto flex items-center justify-center">
                <img
                  src={previewFile?.url || ""}
                  alt={previewFile?.name || t("JournalEntry.attachments.fileFallback")}
                  className="max-h-full max-w-full object-contain rounded-lg"
                />
              </div>
            ) : isPdfPreview ? (
              <iframe
                title={previewFile?.name || "attachment-preview"}
                src={previewFile?.url || ""}
                className="h-full w-full rounded-xl border border-slate-200 bg-white"
              />
            ) : (
              <div className="h-full rounded-xl border border-dashed border-slate-300 bg-white flex flex-col items-center justify-center gap-3 px-4 text-center">
                <p className="text-sm font-medium text-slate-700 break-all">
                  {previewFile?.name || t("JournalEntry.attachments.fileFallback")}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    previewFile?.url &&
                    window.open(previewFile.url, "_blank", "noopener,noreferrer")
                  }
                  className="text-[12px] font-semibold text-blue-700 border border-blue-200 bg-blue-50/60 hover:bg-blue-100 rounded-md px-3 py-1.5 transition"
                >
                  {t("JournalEntry.attachments.view")}
                </button>
              </div>
            )}
          </div>
        </div>
      </MwModal>

      <ConfirmDelete
        title={t("common.deletemsg")}
        msg={`${t("common.deletemsg")} ${fileToDelete?.name || ""}`}
        onDeleteHandler={confirmDeleteFile}
        onCancelHandle={closeDeleteConfirm}
        loading={loadingDeleting}
        onShow={showDeleteConfirm}
        setOnShow={handleDeleteConfirmVisibility}
        type={"delete"}
      />
    </>
  );
};

export default AttachedFiles;
